import React, { useState, useEffect, useCallback } from 'react';
import api, { API_URL } from '../../services/api';
import {
  DndContext, DragOverlay, useDraggable, useDroppable,
  PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  Search, RefreshCw, X, Clock, User, CheckCircle, AlertCircle, Wrench,
  GripVertical, UserPlus,
} from 'lucide-react';

// ── LOT 5-bis (12/06/2026) — "Parcours OF" refondu en ATELIER DE CRAFT.
// 100% front, endpoints existants : /atelier/fabrication (liste), /fabrication/<id>
// (détail ; résout déjà ordre.image_url depuis le bijou lié), /of/<id>/perte-or,
// /atelier/kanban (établis ouvriers), planifier-etape / etapes-adhoc / affecter /
// poids-or (affectation + pesée #2). PHOTO : bascule ordre.image_url||design_file
// -> photo ; sinon silhouette SVG qui se remplit (aucune photo en base à ce jour).

const DONE = ['terminee', 'validee', 'termine'];
const ACTIF = ['en_cours', 'pause', 'en_cours_externe'];
const COULEURS = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-sky-500', 'bg-violet-500', 'bg-teal-500', 'bg-orange-500'];
const couleurOuvrier = (id) => COULEURS[(id || 0) % COULEURS.length];
const initiales = (o) => `${(o.prenom || '')[0] || ''}${(o.nom || '')[0] || ''}`.toUpperCase() || '?';
const metierOuvrier = (o) => { const s = (o.specialite || '').trim(); return s && s.toLowerCase() !== 'autre' ? s : null; };

const fmtH = (sec) => {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m} min`;
};
const fmtG = (v) => (v == null ? '—' : `${parseFloat(v).toFixed(3)} g`);
function chrono(dateDebut, now) {
  if (!dateDebut) return null;
  const sec = Math.max(0, Math.floor((now - new Date(String(dateDebut).replace(' ', 'T'))) / 1000));
  return fmtH(sec);
}

// Type de pièce : ordre.type_bijou -> mots-clés -> préfixe T officiel -> générique.
// Grille T (references.py) : 1=bague · 2=boucles · 3=collier/pendentif · 4=bracelet.
function detecterType(ordre) {
  const texte = `${ordre?.type_bijou || ''} ${ordre?.description || ''} ${ordre?.ref_article || ''}`.toUpperCase();
  if (/BAGUE|SOLITAIRE|ALLIANCE|CHEVALIERE/.test(texte)) return 'bague';
  if (/BOUCLE|CREOLE|PUCE|^BO | BO /.test(texte)) return 'boucles';
  if (/COLLIER|CHAINE|RIVIERE|RAS.DE.COU/.test(texte)) return 'collier';
  if (/BRACELET|MANCHETTE|JONC|GOURMETTE/.test(texte)) return 'bracelet';
  if (/PENDENTIF|MEDAILLE|MEDAILLON|CROIX/.test(texte)) return 'pendentif';
  const m = /OF-(\d)\./.exec(ordre?.numero_ordre || '');
  const parPrefixe = { 1: 'bague', 2: 'boucles', 3: 'collier', 4: 'bracelet' };
  if (m && parPrefixe[m[1]]) return parPrefixe[m[1]];
  return 'generique';
}

// ── Silhouette SVG : partie forgée dorée (+ gemmes colorées), reste gris clair ──
function Silhouette({ type, pct }) {
  const fillH = Math.round(140 * Math.min(1, Math.max(0, pct / 100)));
  const formes = {
    bague: { forme: <g><circle cx="70" cy="86" r="36" fill="none" strokeWidth="10" /><path d="M 53 55 L 70 32 L 87 55 L 70 63 Z" strokeWidth="4" /></g>, gemmes: <circle cx="70" cy="46" r="6" /> },
    boucles: { forme: <g><circle cx="45" cy="75" r="24" fill="none" strokeWidth="7" /><circle cx="95" cy="75" r="24" fill="none" strokeWidth="7" /><circle cx="45" cy="46" r="5" strokeWidth="4" /><circle cx="95" cy="46" r="5" strokeWidth="4" /></g>, gemmes: <g><circle cx="45" cy="96" r="5" /><circle cx="95" cy="96" r="5" /></g> },
    collier: { forme: <g><path d="M 22 36 Q 70 110 118 36" fill="none" strokeWidth="8" /><circle cx="70" cy="92" r="7" strokeWidth="4" /><path d="M 61 99 L 70 122 L 79 99 Z" strokeWidth="5" /></g>, gemmes: <circle cx="70" cy="110" r="5" /> },
    bracelet: { forme: <g><ellipse cx="70" cy="74" rx="44" ry="38" fill="none" strokeWidth="10" strokeDasharray="180 20" /><circle cx="70" cy="36" r="8" strokeWidth="4" /></g>, gemmes: <circle cx="70" cy="36" r="5" /> },
    pendentif: { forme: <g><path d="M 52 22 Q 70 38 88 22" fill="none" strokeWidth="5" /><line x1="70" y1="31" x2="70" y2="56" strokeWidth="4" /><path d="M 70 56 L 91 82 L 70 120 L 49 82 Z" strokeWidth="5" /></g>, gemmes: <circle cx="70" cy="84" r="6" /> },
    generique: { forme: <g><path d="M 44 46 L 96 46 L 111 68 L 70 118 L 29 68 Z" strokeWidth="5" /><path d="M 44 46 L 70 68 L 96 46 M 29 68 L 70 68 L 111 68 M 70 68 L 70 118" strokeWidth="3" /></g>, gemmes: <circle cx="70" cy="58" r="5" /> },
  };
  const f = formes[type] || formes.generique;
  const clipId = `forge-${type}`;
  return (
    <svg viewBox="0 0 140 140" className="w-44 h-44 mx-auto" aria-label={`Silhouette ${type}`}>
      <defs><clipPath id={clipId}><rect x="0" y={140 - fillH} width="140" height={fillH} /></clipPath></defs>
      <g stroke="#e5e7eb" fill="none">{f.forme}</g>
      <g clipPath={`url(#${clipId})`}>
        <g stroke="#d97706" fill="#fcd34d" fillOpacity="0.4">{f.forme}</g>
        <g fill="#a78bfa" stroke="#7c3aed" strokeWidth="1.5">{f.gemmes}</g>
      </g>
    </svg>
  );
}

function Jauge({ label, icone, valeur, sous, pct, couleur }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-3">
      <p className="text-xs font-bold text-gray-500 uppercase">{icone} {label}</p>
      <p className="text-lg font-black text-gray-800 mt-0.5">{valeur}</p>
      {sous && <p className="text-xs text-gray-500">{sous}</p>}
      <div className="mt-1.5 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${couleur}`} style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }} />
      </div>
    </div>
  );
}

// ── Tuile étape (bande horizontale) : draggable si "à lancer" ──
function TuileEtape({ t, now, onLancer }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: t.key, data: { tuile: t }, disabled: t.etat !== 'a_lancer',
  });
  const base = 'flex-shrink-0 w-44 rounded-xl p-2.5 text-left transition-shadow';
  if (t.etat === 'fait') {
    return (
      <div className={`${base} bg-emerald-50 border border-emerald-200`}>
        <p className="text-xs font-bold text-emerald-700 flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" />FAIT</p>
        <p className="text-sm font-semibold text-gray-800 truncate" title={t.nom}>{t.nom}</p>
        <p className="text-xs text-gray-500 truncate">{t.ouvrier || '—'}{t.tempsSec != null ? ` · ${fmtH(t.tempsSec)}` : ''}</p>
      </div>
    );
  }
  if (t.etat === 'en_cours') {
    return (
      <div className={`${base} bg-blue-50 border-2 border-blue-400`}>
        <p className="text-xs font-bold text-blue-700 flex items-center gap-1">▶ EN COURS{t.statut === 'pause' ? ' (⏸)' : ''}</p>
        <p className="text-sm font-semibold text-gray-800 truncate" title={t.nom}>{t.nom}</p>
        <p className="text-xs text-blue-700 truncate">{t.ouvrier || '—'}{t.dateDebut && t.statut !== 'pause' ? ` · ${chrono(t.dateDebut, now)}` : ''}</p>
      </div>
    );
  }
  return (
    <div ref={setNodeRef} className={`${base} bg-white border-2 border-dashed border-gray-300 hover:border-indigo-400 ${isDragging ? 'opacity-40' : ''}`}>
      <div className="flex items-center gap-1">
        <button {...listeners} {...attributes} className="touch-none cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500" title="Glisser sur un établi" aria-label="Glisser">
          <GripVertical className="h-4 w-4" />
        </button>
        <p className="text-xs font-bold text-gray-400">À LANCER</p>
      </div>
      <p className="text-sm font-semibold text-gray-700 truncate" title={t.nom}>{t.nom}</p>
      <button onClick={() => onLancer(t)} className="mt-1 px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 flex items-center gap-1">
        <UserPlus className="h-3 w-3" />lancer chez…
      </button>
    </div>
  );
}

// ── Établi ouvrier (zone de drop) ──
function Etabli({ ouvrier: o, dragActif, now }) {
  const { setNodeRef, isOver } = useDroppable({ id: o.id, data: { ouvrier: o } });
  const enCours = (o.etapes || []).find(e => ACTIF.includes(e.statut));
  const actif = !!enCours;
  const metier = metierOuvrier(o);
  return (
    <div ref={setNodeRef}
      className={`rounded-xl p-2.5 transition-colors border-2 ${isOver ? 'bg-indigo-50 border-indigo-500 ring-2 ring-indigo-300'
        : actif ? 'bg-white border-emerald-300 shadow-sm'
        : dragActif ? 'bg-white border-indigo-200 border-dashed' : 'bg-gray-50/60 border-gray-200 border-dashed'}`}>
      <div className="flex items-center gap-2">
        <div className={`h-9 w-9 rounded-full ${couleurOuvrier(o.id)} text-white flex items-center justify-center text-xs font-bold ${actif ? '' : 'opacity-60'}`}>{initiales(o)}</div>
        <div className="min-w-0">
          <p className="font-semibold text-sm text-gray-800 truncate">{o.prenom} {o.nom}</p>
          {metier && <p className="text-xs text-gray-400 truncate">{metier}</p>}
        </div>
      </div>
      {actif ? (
        <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5">
          <span className="font-mono font-bold text-orange-700">{enCours.numero_ordre}</span>
          <p className="text-gray-700 truncate">{enCours.nom_etape}</p>
          {enCours.statut === 'pause'
            ? <span className="text-amber-600 font-semibold">⏸ en pause</span>
            : enCours.date_debut && <span className="text-emerald-700 font-semibold flex items-center gap-1"><Clock className="h-3 w-3" />{chrono(enCours.date_debut, now)}</span>}
        </div>
      ) : (
        <p className="mt-2 text-xs text-gray-400 text-center py-1.5">libre — déposez une étape</p>
      )}
    </div>
  );
}

export default function ParcoursOF() {
  const [liste, setListe] = useState([]);
  const [kanban, setKanban] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [ofId, setOfId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [perteOr, setPerteOr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [dragTuile, setDragTuile] = useState(null);
  // Modale de lancement : {tuile, ouvrierId|null} ; nom éditable (SAV/adhoc) + poids #2
  const [lancement, setLancement] = useState(null);
  const [lanceNom, setLanceNom] = useState('');
  const [lanceOuvrier, setLanceOuvrier] = useState('');
  const [lancePoids, setLancePoids] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const loadListe = useCallback(() => {
    api.get(`${API_URL}/atelier/fabrication`).then(r => setListe(Array.isArray(r.data) ? r.data : [])).catch(() => setError('Erreur chargement des OF'));
    api.get(`${API_URL}/atelier/kanban`).then(r => setKanban(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadListe(); }, [loadListe]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const charger = useCallback(async (id) => {
    setOfId(id); setDetail(null); setPerteOr(null); setLoading(true); setError('');
    try {
      const r = await api.get(`${API_URL}/atelier/fabrication/${id}`);
      setDetail(r.data);
      if ((r.data?.ordre?.genre || '').toUpperCase() !== 'SAV') {
        api.get(`${API_URL}/atelier/of/${id}/perte-or`).then(p => setPerteOr(p.data)).catch(() => {});
      }
    } catch (e) { setError(e.response?.data?.error || 'Erreur chargement OF'); }
    finally { setLoading(false); }
  }, []);

  const flash = (m) => { setInfo(m); setTimeout(() => setInfo(''), 5000); };

  // ── Sélecteur d'OF : 10 plus récents non terminés + recherche/scan ──
  const q = recherche.trim().toLowerCase();
  const actifs = liste.filter(o => !['termine', 'annule'].includes(o.statut));
  const matches = q
    ? liste.filter(o => [o.numero_ordre, o.ref_article, o.description, o.client_nom].some(v => (v || '').toLowerCase().includes(q)))
    : [...actifs].sort((a, b) => (b.id || 0) - (a.id || 0)).slice(0, 10);
  const onScan = () => {
    if (!q) return;
    const exact = liste.find(o => (o.numero_ordre || '').trim().toLowerCase() === q);
    const cands = exact ? [exact] : matches;
    if (cands.length === 1) charger(cands[0].id);
    else if (cands.length === 0) setError(`Aucun OF ne correspond à « ${recherche.trim()} »`);
  };

  // ── Données dérivées de l'OF sélectionné ──
  const ordre = detail?.ordre || {};
  const estSAV = (ordre.genre || '').toUpperCase() === 'SAV';
  const photo = ordre.image_url || ordre.design_file || null;  // bascule photo -> silhouette
  const etapesReelles = (detail?.etapes || []).slice().sort((a, b) => (a.numero_etape || 0) - (b.numero_etape || 0));
  const nomsReels = new Set(etapesReelles.map(e => (e.nom_etape || '').trim().toLowerCase()));
  const aVenirDevis = (detail?.etapes_devis || []).filter(e =>
    !e.id && e.statut !== 'non_requis' && e.du_catalogue !== true &&
    (e.est_etape_devis || e.in_devis) && !nomsReels.has((e.nom_etape || '').trim().toLowerCase()));
  // Pseudo-étapes "nom d'ouvrier" (import legacy) : jamais proposées au lancement (fix #1)
  const nomsOuvriers = new Set((kanban?.liste_ouvriers || []).flatMap(o =>
    [o.nom, o.prenom, `${o.prenom || ''} ${o.nom || ''}`].filter(Boolean).map(s => s.trim().toLowerCase()).filter(s => s)));

  // Tuiles de la bande d'étapes
  const tuiles = [];
  etapesReelles.forEach(e => {
    const etat = DONE.includes(e.statut) ? 'fait' : ACTIF.includes(e.statut) ? 'en_cours' : 'a_lancer';
    if (etat === 'a_lancer' && nomsOuvriers.has((e.nom_etape || '').trim().toLowerCase())) return;
    tuiles.push({
      key: `e-${e.id}`, etat, nom: e.nom_etape, statut: e.statut,
      ouvrier: e.ouvrier_nom || null, dateDebut: e.date_debut,
      tempsSec: e.duree_reelle_heures != null ? parseFloat(e.duree_reelle_heures) * 3600 : null,
      mode: 'affecter', etapeId: e.id,
    });
  });
  aVenirDevis.forEach((e, i) => tuiles.push({
    key: `d-${i}`, etat: 'a_lancer', nom: e.nom_etape, mode: 'planifier',
  }));
  // OF SANS étape réelle (SAV + FAB importés) et aucune tuile "à lancer" : le TRAVAIL
  // (consigne = colonne CONSIGNE du SUIVI xlsm ; description = nom de la pièce) devient
  // la tuile à lancer pré-remplie (etapes-adhoc), éditable.
  if (detail && etapesReelles.length === 0 && !tuiles.some(t => t.etat === 'a_lancer')) {
    tuiles.push({ key: 'travail', etat: 'a_lancer', nom: ordre.consigne || ordre.description || 'travail à définir', mode: 'adhoc', editable: true });
  }

  const nbTerm = tuiles.filter(t => t.etat === 'fait').length;
  const nbEnCours = tuiles.filter(t => t.etat === 'en_cours').length;
  const nbAVenir = tuiles.filter(t => t.etat === 'a_lancer').length;
  const total = nbTerm + nbEnCours + nbAVenir;
  const pct = total > 0 ? Math.round(nbTerm / total * 100) : 0;

  // Jauges
  const orDonnes = etapesReelles.map(e => e.poids_or_donne).filter(v => v != null);
  const orRendus = etapesReelles.map(e => e.poids_or_recu).filter(v => v != null);
  const orDonne = orDonnes.length ? orDonnes.reduce((s, v) => s + parseFloat(v), 0) : null;
  const orRendu = orRendus.length ? orRendus.reduce((s, v) => s + parseFloat(v), 0) : null;
  const orEcart = (orDonne != null && orRendu != null) ? +(orDonne - orRendu).toFixed(3) : null;
  const pierresPrevues = (detail?.pierres || []).length || null;
  let pierresSerties = 0, pierresInfo = false;
  etapesReelles.forEach(e => {
    if (e.pierres_remises_json) {
      try { const arr = JSON.parse(e.pierres_remises_json); if (Array.isArray(arr)) { pierresSerties += arr.length; pierresInfo = true; } } catch (err) { /* ignore */ }
    }
  });
  let tempsSec = 0, tempsInfo = false;
  etapesReelles.forEach(e => {
    if (e.duree_reelle_heures != null) { tempsSec += parseFloat(e.duree_reelle_heures) * 3600; tempsInfo = true; }
    else if (ACTIF.includes(e.statut) && e.elapsed_seconds != null) { tempsSec += parseInt(e.elapsed_seconds); tempsInfo = true; }
  });
  const type = detecterType(ordre);

  // ── Lancement (drop OU clic "lancer chez…") -> modale nom/ouvrier/poids ──
  const ouvrirLancement = (tuile, ouvrierId) => {
    setLancement({ tuile, ouvrierId: ouvrierId || null });
    setLanceNom(tuile.nom || ''); setLanceOuvrier(''); setError('');
    // suggestion poids (#2) : dernier or rendu d'une étape, sinon poids or estimé OF
    const dernierRendu = [...etapesReelles].reverse().map(e => e.poids_or_recu).find(v => v != null);
    const sugg = dernierRendu ?? ordre.poids_or_estime ?? null;
    setLancePoids(sugg != null && parseFloat(sugg) > 0 ? parseFloat(sugg).toFixed(3) : '');
  };
  const doLancer = async () => {
    const t = lancement.tuile;
    const ouvrierId = lancement.ouvrierId || lanceOuvrier;
    if (!ouvrierId) { setError('Choisir un ouvrier'); return; }
    const nom = (lanceNom || '').trim();
    if (!nom) { setError("Décrire l'étape"); return; }
    setBusy(true); setError('');
    try {
      let etapeId, warning;
      if (t.mode === 'affecter') {
        const r = await api.post(`${API_URL}/atelier/etapes/${t.etapeId}/affecter`, { ouvrier_id: parseInt(ouvrierId) });
        etapeId = t.etapeId; warning = r.data?.warning;
      } else if (t.mode === 'planifier') {
        const r = await api.post(`${API_URL}/atelier/ordres-fabrication/${ofId}/planifier-etape`, { nom_etape: nom, ouvrier_id: parseInt(ouvrierId) });
        etapeId = r.data?.etape_id; warning = r.data?.warning;
      } else {
        const r1 = await api.post(`${API_URL}/atelier/ordres-fabrication/${ofId}/etapes-adhoc`, { nom_etape: nom, type_ressource: 'interne', duree_estimee_heures: 1 });
        etapeId = r1.data?.etape_production_id || r1.data?.id;
        await api.post(`${API_URL}/atelier/etapes/${etapeId}/affecter`, { ouvrier_id: parseInt(ouvrierId) });
      }
      // poids #2 (optionnel) — échec = affectation conservée
      const val = parseFloat(lancePoids);
      if (etapeId && lancePoids && !isNaN(val) && val > 0) {
        try { await api.put(`${API_URL}/atelier/etapes/${etapeId}/poids-or`, { poids_or_donne: val }); }
        catch (e2) { setError('Lancé, mais poids non enregistré — ressaisissez-le dans la fiche OF.'); }
      }
      flash(`Étape « ${nom} » lancée${warning ? ' — ⚠️ ' + warning : ''}`);
      setLancement(null);
      await charger(ofId); loadListe();
    } catch (e) { setError(e.response?.data?.message || e.response?.data?.error || 'Erreur lancement'); }
    finally { setBusy(false); }
  };

  const onDragStart = (ev) => setDragTuile(ev.active?.data?.current?.tuile || null);
  const onDragEnd = (ev) => {
    const t = ev.active?.data?.current?.tuile; setDragTuile(null);
    const ouvrierId = ev.over?.id;
    if (!t || !ouvrierId) return;
    ouvrirLancement(t, ouvrierId);
  };

  const ouvriers = kanban?.ouvriers || [];

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="space-y-3">
        {/* Sélecteur d'OF */}
        <div className="bg-white rounded-xl border shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h2 className="font-bold text-gray-800">🧭 Parcours OF</h2>
            {info && <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded flex items-center gap-1"><CheckCircle className="h-3 w-3" />{info}</span>}
            <div className="relative flex-1 max-w-md ml-auto">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input value={recherche} onChange={e => setRecherche(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onScan(); }}
                placeholder="📷 Scanner ou chercher un N° OF…"
                className="w-full pl-8 pr-2 py-2 border rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {matches.map(o => (
              <button key={o.id} onClick={() => charger(o.id)}
                className={`px-2.5 py-1.5 rounded-lg border text-xs font-mono font-semibold ${ofId === o.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-orange-700 hover:bg-indigo-50'}`}
                title={o.ref_article || o.description || ''}>
                {o.numero_ordre}
              </button>
            ))}
            {matches.length === 0 && <p className="text-xs text-gray-400">Aucun OF.</p>}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center justify-between"><span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</span><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}
        {loading && <div className="p-8 text-center text-gray-400"><RefreshCw className="h-6 w-6 animate-spin mx-auto" /></div>}

        {detail && !loading && (
          /* ── L'ÉTABLI ── */
          <div className="bg-gradient-to-b from-stone-100 to-stone-50 rounded-2xl border-2 border-stone-200 p-4 space-y-4">
            {/* En-tête */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-lg font-black text-orange-700">{ordre.numero_ordre}</span>
              <span className="text-gray-800 font-semibold">{ordre.ref_article || ordre.description || '—'}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold uppercase">{estSAV ? '🔧 SAV' : type}</span>
              {ordre.metal && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">{ordre.metal}</span>}
              {ordre.client_nom && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex items-center gap-1"><User className="h-3 w-3" />{ordre.client_nom}</span>}
              <div className="ml-auto flex items-center gap-3">
                <div className="flex gap-1.5 text-xs font-bold">
                  <span className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700">✅ {nbTerm}</span>
                  <span className="px-2 py-1 rounded-lg bg-blue-100 text-blue-700">▶ {nbEnCours}</span>
                  <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-500">○ {nbAVenir}</span>
                </div>
                <span className="text-3xl font-black text-indigo-700">{pct}%<span className="text-sm font-bold text-indigo-400"> forgé</span></span>
              </div>
            </div>

            {/* Zone centrale : pièce + jauges */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white rounded-xl border shadow-sm p-3 flex flex-col items-center justify-center">
                {photo
                  ? <img src={photo} alt={ordre.ref_article || 'bijou'} className="max-h-44 rounded-lg object-contain" />
                  : <Silhouette type={estSAV ? 'generique' : type} pct={pct} />}
                <p className="text-xs text-gray-400 mt-1">{photo ? 'photo de la pièce' : 'silhouette — pas de photo'}</p>
              </div>
              <div className="space-y-2">
                {estSAV ? (
                  <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 flex items-center gap-3 h-full">
                    <Wrench className="h-8 w-8 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-amber-800">SAV / réparation</p>
                      <p className="text-sm text-amber-700">Suivi or/pierres non applicable.{ordre.description ? ` Travail : ${ordre.description}` : ''}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Jauge label="Or" icone="⚖️" couleur="bg-amber-400"
                      valeur={orDonne != null ? fmtG(orDonne) : (perteOr?.poids_or_initial != null ? fmtG(perteOr.poids_or_initial) : '—')}
                      sous={orEcart != null ? `rendu ${fmtG(orRendu)} · écart ${orEcart > 0 ? '−' : '+'}${Math.abs(orEcart).toFixed(3)} g`
                        : (perteOr?.perte_totale_g != null ? `final ${fmtG(perteOr.poids_final_bijou)} · perte ${perteOr.perte_totale_g} g` : 'donné / rendu non saisis')}
                      pct={orDonne != null && orRendu != null ? (orRendu / orDonne) * 100 : (orDonne != null || perteOr?.poids_or_initial != null ? 100 : 0)} />
                    <Jauge label="Pierres" icone="💎" couleur="bg-purple-400"
                      valeur={pierresInfo || pierresPrevues ? `${pierresSerties}${pierresPrevues ? ` / ${pierresPrevues}` : ' serties'}` : '—'}
                      sous={pierresPrevues ? 'serties / prévues au devis' : (pierresInfo ? 'serties (pas de devis)' : 'aucune donnée pierres')}
                      pct={pierresPrevues ? (pierresSerties / pierresPrevues) * 100 : (pierresInfo ? 100 : 0)} />
                    <Jauge label="Temps" icone="⏱" couleur="bg-emerald-400"
                      valeur={tempsInfo ? fmtH(tempsSec) : '—'}
                      sous={nbEnCours > 0 ? 'chrono en cours…' : (tempsInfo ? 'cumul des étapes' : 'temps non saisis')}
                      pct={tempsInfo ? 100 : 0} />
                  </>
                )}
              </div>
            </div>

            {/* Bande d'étapes */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-1.5 flex items-center gap-1"><GripVertical className="h-3.5 w-3.5" />Étapes — glissez-en une sur un ouvrier pour la lancer</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {tuiles.length === 0 ? <p className="text-sm text-gray-400 py-2">Aucune étape.</p>
                  : tuiles.map(t => <TuileEtape key={t.key} t={t} now={now} onLancer={(tt) => ouvrirLancement(tt, null)} />)}
              </div>
            </div>

            {/* Établis ouvriers */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-1.5">Ateliers — déposez l'étape sur l'établi d'un ouvrier</p>
              <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                {ouvriers.map(o => <Etabli key={o.id} ouvrier={o} dragActif={!!dragTuile} now={now} />)}
              </div>
            </div>
          </div>
        )}
        {!detail && !loading && <p className="text-sm text-gray-400 text-center py-6">Choisissez un OF ci-dessus (clic, recherche ou scan douchette).</p>}

        {/* Overlay : la tuile suit le curseur */}
        <DragOverlay>
          {dragTuile && (
            <div className="w-44 rounded-xl p-2.5 bg-white border-2 border-indigo-400 shadow-lg">
              <p className="text-xs font-bold text-indigo-500">À LANCER</p>
              <p className="text-sm font-semibold text-gray-800 truncate">{dragTuile.nom}</p>
            </div>
          )}
        </DragOverlay>

        {/* Modale de lancement (nom éditable si adhoc/SAV + ouvrier + poids #2) */}
        {lancement && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] overflow-y-auto">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
                <h3 className="text-lg font-semibold">Lancer l'étape</h3>
                <button onClick={() => setLancement(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-6 space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Étape{lancement.tuile.editable ? ' (modifiable)' : ''}</label>
                  {lancement.tuile.editable || lancement.tuile.mode === 'adhoc'
                    ? <input type="text" value={lanceNom} onChange={e => setLanceNom(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                    : <p className="px-3 py-2 bg-gray-50 rounded-lg text-sm font-semibold text-gray-800">{lanceNom}</p>}
                </div>
                {!lancement.ouvrierId && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Ouvrier *</label>
                    <select value={lanceOuvrier} onChange={e => setLanceOuvrier(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">-- Choisir --</option>
                      {(kanban?.liste_ouvriers || []).map(o => <option key={o.id} value={o.id}>{o.prenom} {o.nom}{metierOuvrier(o) ? ` (${metierOuvrier(o)})` : ''}</option>)}
                    </select>
                  </div>
                )}
                {lancement.ouvrierId && (() => {
                  const o = (kanban?.liste_ouvriers || []).find(x => x.id === lancement.ouvrierId);
                  return o ? <p className="text-sm text-gray-700">Chez <span className="font-bold">{o.prenom} {o.nom}</span></p> : null;
                })()}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">⚖️ Poids métal remis (g) — optionnel</label>
                  <input type="text" inputMode="decimal" value={lancePoids}
                    onChange={e => setLancePoids(e.target.value.replace(',', '.'))}
                    onFocus={e => e.target.select()} placeholder="laisser vide si pas de métal"
                    className="w-full px-3 py-2 border rounded-lg font-bold" />
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
                <button onClick={() => setLancement(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
                <button onClick={doLancer} disabled={busy} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />{busy ? '...' : 'Lancer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
}
